/**
 * "A newer frontend is out" (Ben, 2026-10-22). Every build is stamped
 * (__BUILD_ID__, vite.config.ts) and publishes the same stamp at
 * /version.json. An open tab asks for it now and then — and whenever the
 * tab comes back into view — and once the answer differs from the stamp it
 * was built with, the sidebar offers a refresh. It never reloads by itself:
 * a page with unsaved work must not vanish under someone.
 *
 * Quiet on failure: no version.json (an older build), a network blip or a
 * non-JSON answer all read as "nothing to say", never as an update.
 */
import { useEffect, useRef, useState } from 'react'

/** How often an open, visible tab asks. */
export const VERSION_POLL_MS = 2 * 60 * 1000

/** A deployed build other than this one, from /version.json's body. */
export function isNewerBuild(body: unknown, current: string): boolean {
  const build = (body as { build?: unknown } | null)?.build
  return typeof build === 'string' && build.length > 0 && build !== current
}

export async function checkForNewBuild(current: string): Promise<boolean> {
  try {
    // A static file the frontend image serves itself (gen-nginx.mjs), never
    // an API route. api-surface: not-a-call
    const r = await fetch('/version.json', {
      cache: 'no-store', credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!r.ok) return false
    return isNewerBuild(await r.json(), current)
  } catch {
    return false
  }
}

/** True once a newer frontend has been deployed than the one running. */
export function useNewVersion(current: string = __BUILD_ID__,
                              pollMs: number = VERSION_POLL_MS): boolean {
  const [newer, setNewer] = useState(false)
  useEffect(() => {
    if (newer) return
    let live = true
    const check = () => {
      if (document.visibilityState === 'hidden') return
      void checkForNewBuild(current).then((n) => { if (live && n) setNewer(true) })
    }
    check()
    const timer = window.setInterval(check, pollMs)
    const onVisible = () => { if (document.visibilityState === 'visible') check() }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', check)
    return () => {
      live = false
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', check)
    }
  }, [current, pollMs, newer])
  return newer
}

/** Once a newer build is known, the NEXT in-app navigation becomes a full
 *  page load of where the user was going (Ben, 2026-10-23: tabs left open
 *  for days kept running old frontends). Never a reload in place: leaving
 *  the page already drops its unsaved state, so this costs nothing the
 *  click did not. `where` is the router location (pathname + search).
 *  `load` is the seam for tests. */
export function useLoadNewBuildOnNavigate(
  newer: boolean, where: string,
  load: (href: string) => void = (href) => window.location.assign(href),
): void {
  const seenAt = useRef<string | null>(null)
  useEffect(() => {
    if (!newer) { seenAt.current = null; return }
    if (seenAt.current === null) { seenAt.current = where; return }
    if (where !== seenAt.current) load(where)
  }, [newer, where, load])
}
